import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { X, Save, Loader2, Plus, Trash2, Building2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PhoneField } from "@/components/PhoneField";
import { API, buildHeaders } from "./shared";

const Field = ({ label, value, onChange, placeholder, testid, type = "text", phone }) => (
  <label className="block">
    <div className="text-[10.5px] font-bold tracking-[0.10em] uppercase text-[#94a3b8] mb-1">{label}</div>
    {phone ? (
      <PhoneField value={value} onChange={(v) => onChange(v)} placeholder={placeholder} testid={testid} />
    ) : (
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-9 px-3 rounded-md border border-[#e5e7eb] bg-white text-[13px] text-[#0f172a]"
        data-testid={testid}
      />
    )}
  </label>
);

const emptyLeader = (school_id) => ({
  __new: true,
  __tmpId: `tmp_${Math.random().toString(36).slice(2, 10)}`,
  school_id,
  leader_name: "",
  leader_role: "",
  email: "",
  phone: "",
  verified: false,
});

const ROLE_HINTS = ["Lead Principal/CSA", "Superintendent", "Business Administrator", "Board Chair", "Board Member", "Executive Director", "Director of Special Education"];

export default function EditSchoolModal({ open, onClose, school, leaders, session, onSaved }) {
  const [s, setS] = useState(null);
  const [ls, setLs] = useState([]);
  const [busy, setBusy] = useState(false);
  const headersAuth = useMemo(() => buildHeaders(session), [session]);

  useEffect(() => {
    if (open && school) {
      setS({ ...school });
      setLs((leaders || []).map((L) => ({ ...L })));
    }
  }, [open, school, leaders]);

  if (!open || !s) return null;

  const updateSchool = (field, value) => setS((prev) => ({ ...prev, [field]: value }));
  const updateLeader = (idx, field, value) =>
    setLs((prev) => prev.map((L, i) => (i === idx ? { ...L, [field]: value } : L)));
  const addLeader = () => setLs((prev) => [...prev, emptyLeader(s.school_id)]);
  const removeLeader = (idx) => setLs((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setBusy(true);
    try {
      // 1) Save school
      await axios.patch(`${API}/api/admin/charter-schools/${s.school_id}`, {
        school_name: s.school_name,
        city: s.city,
        state: s.state,
        county: s.county,
        website: s.website,
      }, { headers: headersAuth });

      // 2) Compute which existing leaders the user removed (in the original
      //    `leaders` prop but absent from current `ls`) → call DELETE.
      const stillPresent = new Set(ls.filter((L) => !L.__new && L.id).map((L) => L.id));
      const toDelete = (leaders || []).filter((L) => L.id && !stillPresent.has(L.id));
      for (const L of toDelete) {
        await axios.delete(`${API}/api/admin/charter-leaders/${L.id}`, { headers: headersAuth });
      }

      // 3) PATCH existing leaders (name/role/email/phone/verified)
      for (const L of ls) {
        if (L.__new) continue;
        await axios.patch(`${API}/api/admin/charter-leaders/${L.id}`, {
          leader_name: L.leader_name,
          leader_role: L.leader_role,
          email: L.email || "",
          phone: L.phone || "",
          verified: !!L.verified,
        }, { headers: headersAuth });
      }

      // 4) Create newly-added leaders via the CSV importer (single-row CSV per leader)
      const newLeaders = ls.filter((L) => L.__new && L.leader_name.trim() && L.leader_role.trim());
      if (newLeaders.length) {
        const csv = [
          "school_id,school_name,leader_name,leader_role,leader_email,leader_phone,verified",
          ...newLeaders.map((L) => [
            s.school_id,
            JSON.stringify(s.school_name || "").slice(1, -1).replace(/,/g, " "),
            JSON.stringify(L.leader_name || "").slice(1, -1).replace(/,/g, " "),
            JSON.stringify(L.leader_role || "").slice(1, -1).replace(/,/g, " "),
            (L.email || "").replace(/,/g, " "),
            (L.phone || "").replace(/,/g, " "),
            L.verified ? "Y" : "N",
          ].join(",")),
        ].join("\n");
        await axios.post(`${API}/api/admin/campaigns/import-csv`, { csv }, { headers: headersAuth });
      }

      const parts = [];
      if (toDelete.length) parts.push(`${toDelete.length} deleted`);
      if (newLeaders.length) parts.push(`${newLeaders.length} added`);
      parts.push("changes saved");
      toast.success(parts.join(" · "));
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[115] flex items-center justify-center bg-black/55 p-3 sm:p-4" data-testid="edit-school-modal">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: "#0b1220", color: "#fff" }}>
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-5 w-5 text-[#C79A3B] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#C79A3B]">Edit School & Leaders</div>
              <h2 className="text-[15px] sm:text-[16px] font-bold truncate" data-testid="edit-school-title">
                {s.school_name}
              </h2>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white shrink-0" data-testid="edit-school-close-btn">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 sm:px-6 py-5 overflow-y-auto flex-1 min-h-0 space-y-5">
          {/* School block */}
          <section data-testid="edit-school-school-section">
            <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-[#94a3b8] mb-2">School</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="School name" value={s.school_name} onChange={(v) => updateSchool("school_name", v)} testid="edit-school-name" />
              <Field label="Website" value={s.website} onChange={(v) => updateSchool("website", v)} placeholder="example.org" testid="edit-school-website" />
              <Field label="City" value={s.city} onChange={(v) => updateSchool("city", v)} testid="edit-school-city" />
              <Field label="County" value={s.county} onChange={(v) => updateSchool("county", v)} testid="edit-school-county" />
              <Field label="State" value={s.state} onChange={(v) => updateSchool("state", v.toUpperCase())} testid="edit-school-state" />
            </div>
          </section>

          {/* Leaders block */}
          <section data-testid="edit-school-leaders-section">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-[#94a3b8]">Leaders ({ls.length})</div>
              <button type="button" onClick={addLeader}
                className="inline-flex items-center gap-1 px-2 h-7 rounded-md border border-dashed border-[#cbd5e1] text-[11px] font-semibold text-[#475569] hover:bg-[#f8fafc]"
                data-testid="edit-school-add-leader-btn">
                <Plus className="h-3 w-3" /> Add leader
              </button>
            </div>
            <div className="space-y-3">
              {ls.map((L, idx) => (
                <div key={L.id || L.__tmpId} className="rounded-lg border p-3" style={{ borderColor: "#e5e7eb" }}
                  data-testid={`edit-leader-${L.id || L.__tmpId}`}>
                  <div className="flex items-start gap-2 mb-2">
                    <User className="h-3.5 w-3.5 mt-1 text-[#94a3b8]" />
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      <Field label="Name" value={L.leader_name} onChange={(v) => updateLeader(idx, "leader_name", v)} testid={`edit-leader-name-${idx}`} />
                      <Field label="Role" value={L.leader_role} onChange={(v) => updateLeader(idx, "leader_role", v)} placeholder={ROLE_HINTS[idx % ROLE_HINTS.length]} testid={`edit-leader-role-${idx}`} />
                      <Field label="Email" value={L.email} onChange={(v) => updateLeader(idx, "email", v)} placeholder="name@school.org" type="email" testid={`edit-leader-email-${idx}`} />
                      <Field label="Phone" value={L.phone} onChange={(v) => updateLeader(idx, "phone", v)} placeholder="555-555-1234" phone testid={`edit-leader-phone-${idx}`} />
                    </div>
                    <button type="button" onClick={() => removeLeader(idx)}
                      className="p-1.5 rounded-md text-[#b91c1c] hover:bg-[#fee2e2] shrink-0" title="Remove this leader (saved on Save changes)"
                      data-testid={`edit-leader-remove-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={!!L.verified} onChange={(e) => updateLeader(idx, "verified", e.target.checked)}
                      className="h-3.5 w-3.5" data-testid={`edit-leader-verified-${idx}`} />
                    <span className="text-[11.5px] text-[#475569] font-semibold">Verified contact</span>
                  </label>
                </div>
              ))}
              {ls.length === 0 && (
                <div className="text-center py-6 text-[#94a3b8] text-[13px]">No leaders. Click <strong>Add leader</strong> to start.</div>
              )}
            </div>
          </section>
        </div>

        <div className="px-5 sm:px-6 py-3 flex items-center justify-end gap-2 border-t" style={{ borderColor: "#e5e7eb" }}>
          <Button type="button" variant="outline" onClick={onClose} data-testid="edit-school-cancel-btn">Cancel</Button>
          <Button type="button" onClick={save} disabled={busy}
            className="bg-[#16a34a] hover:bg-[#15803d] text-white disabled:opacity-50"
            data-testid="edit-school-save-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
